import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  Action,
  CountBy,
  EntrySectionUpdateEvent,
  FiltersOperator,
  MetricEntryBehavior,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {QueryOptionsChangeEvent} from 'common/query-builder/conversion-query-options/types';
import {mapEventEntryToBookmarkExclusionStep} from 'common/report/funnels/models/format';
import {
  isGroupedEntry,
  MetricEntry,
  QueryEventEntry,
  QueryGroupedEntry,
  QuerySuperEntry,
} from 'common/query-builder/query-entry-section/types';
import {BaseEntryRow} from 'common/query-builder/query-entry-section/row-types/base';
import {RangeSelection} from 'common/widgets/step-range-selector/types';
import {MetricType} from 'common/query-builder/query-block/constants';
import {EventItem} from 'common/query-builder/query-block/events-menu/types';
import {FunnelOrder} from 'common/report/funnels/models/constants';
import {FUNNELS_MATH_TYPES} from 'common/report/constants';
import {ConversionWindowUnit} from 'common/report/funnels/models/constants';
import {renameEvent} from 'common/query-builder/cohorts-query-builder/util';
import {ResourceType} from 'common/report/constants';

// eslint-disable-next-line import/no-cycle
import {FunnelsMetricEntryRowEl} from './template';

export class FunnelsMetricEntryRow extends BaseEntryRow<MetricEntry> {
  template = FunnelsMetricEntryRowEl;

  isPlaceholder() {
    const behaviors = this.entry.behavior.behaviors || [];
    return !this.hasValidSteps() || behaviors.some((behavior) => !this.hasValidFilters(behavior));
  }

  hasValidSteps() {
    const behaviors = this.entry.behavior.behaviors || [];
    return behaviors.length >= 2 || behaviors.every((behavior) => !!behavior.name);
  }

  hasValidFilters(behavior: MetricEntryBehavior) {
    if (!behavior.filters) {
      return true;
    }
    return behavior.filters.every((filter) => new PropertyFilter(filter).isValid());
  }

  getFunnelsSpecificBehavior() {
    const behavior = this.entry.behavior;
    const {funnelOrder, exclusions, aggregateBy, conversionWindowUnit, conversionWindowDuration} = behavior;
    return {
      funnelOrder,
      exclusions,
      aggregateBy,
      conversionWindowUnit,
      conversionWindowDuration,
    };
  }

  convertBehaviorToEntry(
    behavior: MetricEntryBehavior,
    {skipEntryIndexToggle = false}: {skipEntryIndexToggle?: boolean} = {},
  ): QueryEventEntry {
    return {
      ...(skipEntryIndexToggle ? {} : {entryIndexLabelToggle: behavior.funnelOrder === FunnelOrder.Any ? true : false}),
      event: {
        custom: behavior.type === MetricType.CustomEvent,
        id: behavior.id as number,
        label: renameEvent(behavior.name) || `Select Step`,
        renamed: behavior.renamed,
        value: behavior.name,
      },
      filters: behavior.filters,
      filtersOperator: behavior.filtersOperator,
      type: ResourceType.Event,
    };
  }

  convertEventToBehavior(
    event: EventItem,
    entry: QueryEventEntry,
    {skipFunnelOrder = false}: {skipFunnelOrder?: boolean} = {},
  ): MetricEntryBehavior {
    return {
      id: event.custom ? event.id : null,
      type: event.custom ? MetricType.CustomEvent : MetricType.Event,
      name: event.value,
      renamed: event.renamed,
      filters: entry.filters,
      filtersDeterminer: entry.filtersOperator === FiltersOperator.Or ? `any` : `all`,
      ...(skipFunnelOrder ? {} : {funnelOrder: entry.entryIndexLabelToggle ? FunnelOrder.Any : FunnelOrder.Loose}),
    };
  }

  getEntries() {
    const entries = this.entry.behavior.behaviors.map((behavior) => {
      const entryIndexLabelToggle = behavior.funnelOrder === FunnelOrder.Any ? true : false;
      // handle event comparisons
      if (`behaviors` in behavior) {
        const eventComparisonBehaviors = behavior[`behaviors`];
        const events = eventComparisonBehaviors.map((behavior) =>
          this.convertBehaviorToEntry(behavior, {skipEntryIndexToggle: true}),
        );
        const newEntry: QueryGroupedEntry = {
          entryIndexLabelToggle,
          event: events,
          filters: [],
          type: SelectorTypeDefinitions.GroupedEvent,
        };
        return newEntry;
      } else {
        return this.convertBehaviorToEntry(behavior);
      }
    });
    return entries as QuerySuperEntry[];
  }

  handleEntryUpdate = (ev: EntrySectionUpdateEvent<QueryEventEntry>) => {
    const {entries} = ev.detail;
    const behaviors: MetricEntryBehavior[] = [];

    for (const entry of entries) {
      const groupedEntry = isGroupedEntry(entry);
      if (!groupedEntry) {
        const event = entry.event;
        behaviors.push(this.convertEventToBehavior(event, entry));
      } else {
        const newBehaviors = [];
        const event = (entry as QueryGroupedEntry).event;
        for (const subEvent of event) {
          const event = subEvent.event;
          newBehaviors.push(this.convertEventToBehavior(event, subEvent, {skipFunnelOrder: true}));
        }
        behaviors.push({
          behaviors: newBehaviors,
          funnelOrder: (entry as QueryGroupedEntry).entryIndexLabelToggle ? FunnelOrder.Any : FunnelOrder.Loose,
        } as MetricEntryBehavior);
      }
    }

    const entry: MetricEntry = {
      ...this.entry,
      behavior: {
        ...this.entry.behavior,
        behaviors,
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };

  handleFunnelsConversionCriterialChanged = (ev: QueryOptionsChangeEvent) => {
    const {exclusionSteps, funnelOrder, holdingPropertyConstant, conversionWindow} = ev.detail.options;
    const behavior = {...this.entry.behavior};

    if (exclusionSteps) {
      behavior.exclusions = exclusionSteps.map((step) => {
        const subBehaviors = behavior.behaviors;
        return mapEventEntryToBookmarkExclusionStep(subBehaviors.length, step);
      });
    }
    if (funnelOrder) {
      behavior.funnelOrder = funnelOrder;
      behavior.behaviors = behavior.behaviors.map((subBehavior, i) => ({
        ...subBehavior,
        funnelOrder: i === 0 ? FunnelOrder.Loose : funnelOrder,
      }));
    }
    if (holdingPropertyConstant) {
      behavior.aggregateBy = holdingPropertyConstant;
    }
    if (conversionWindow) {
      behavior.conversionWindowDuration = conversionWindow.value;
      behavior.conversionWindowUnit = conversionWindow.unit;
    }

    const entry = {
      ...this.entry,
      behavior,
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };

  handleFunnelsCountBySelected = (ev: CustomEvent<{item: CountBy<RangeSelection>}>) => {
    const countBy = ev.detail.item;
    const {aggregationOperator, property, advancedMenuValue} = countBy;

    let conversionWindowDuration = this.entry.behavior.conversionWindowDuration;
    let conversionWindowUnit = this.entry.behavior.conversionWindowUnit;
    if (aggregationOperator === FUNNELS_MATH_TYPES.SESSION) {
      conversionWindowDuration = 1;
      conversionWindowUnit = ConversionWindowUnit.Session;
    }

    const entry = {
      ...this.entry,
      behavior: {
        ...this.entry.behavior,
        conversionWindowDuration,
        conversionWindowUnit,
      },
      measurement: {
        ...this.entry.measurement,
        aggregationOperator,
        property,
        stepIndex: advancedMenuValue?.isEntireRange ? null : advancedMenuValue?.range?.from,
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };
}
